/**
 * Weg-Mittellinie: reine Helfer (#720)
 *
 * Der Weg wird künftig als offene Mittellinie + Breite modelliert (statt als
 * geschlossene Fläche). Diese Funktionen erzeugen aus der Mittellinien-Punktliste
 * die SVG-Pfaddaten und berechnen Länge/Fläche/Umfang — ohne Fabric, ohne 3D,
 * ohne Seiteneffekte, damit sie isoliert testbar sind.
 *
 * Das gezeichnete Band entsteht nicht hier, sondern in der 2D-Darstellung als
 * gestrichener Pfad (`strokeWidth = Wegbreite`); die Mittellinie ist die Seele.
 */

import { pointsToBezierPath } from './BezierTool';

/**
 * Verlaufsart der Mittellinie.
 * - `eckig`: reine Polylinie (M L L …), harte Ecken an jedem Punkt.
 * - `kurvig`: geglättete Catmull-Rom-Kurve (offener Bezier-Pfad).
 */
export const WEG_VERLAUF = Object.freeze({
  ECKIG: 'eckig',
  KURVIG: 'kurvig',
});

const VALID_VERLAUF = new Set(Object.values(WEG_VERLAUF));

/** Prüft, ob ein Verlaufswert zum erlaubten Vokabular gehört. */
export const isValidVerlauf = (verlauf) => VALID_VERLAUF.has(verlauf);

/**
 * Erzeugt SVG-Pfaddaten für die offene Mittellinie.
 *
 * Immer offen (kein `Z`) — ein Weg ist keine geschlossene Fläche. Bei `kurvig`
 * nutzt die Funktion den offenen Zweig von `pointsToBezierPath`, der Start- und
 * Endpunkt dupliziert (Tangente null), sodass die Kurve sauber an den Enden
 * ausläuft. Der gerade *Abschluss* (flache Endkappe) ist keine Pfad-, sondern
 * eine Stroke-Eigenschaft (`strokeLineCap: 'butt'`) und liegt in der Darstellung.
 *
 * @param {Array<{x: number, y: number}>} points - Mittellinien-Punkte
 * @param {string} verlauf - WEG_VERLAUF.ECKIG | WEG_VERLAUF.KURVIG
 * @returns {string|null} SVG-Pfad-String oder null bei < 2 Punkten
 */
export const centerlineToPathData = (points, verlauf = WEG_VERLAUF.KURVIG) => {
  if (!points || points.length < 2) {
    return null;
  }

  if (verlauf === WEG_VERLAUF.ECKIG) {
    let pathData = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      pathData += ` L ${points[i].x} ${points[i].y}`;
    }
    return pathData;
  }

  // kurvig — offener Bezier (pointsToBezierPath zeichnet für < 4 Punkte selbst
  // Geraden, ab 4 Punkten eine geglättete Kurve).
  return pointsToBezierPath(points, false);
};

/**
 * Länge der Mittellinie in Pixeln (Polygonzug-Näherung).
 *
 * Summe der geraden Segmentlängen. Für `kurvig` ist die echte Bogenlänge minimal
 * größer; die Polygonzug-Länge ist der übliche, günstige Näherungswert und für
 * Flächen-/Umfangsanzeige ausreichend genau.
 *
 * @param {Array<{x: number, y: number}>} points - Mittellinien-Punkte
 * @returns {number} Länge in Pixeln (0 bei < 2 Punkten)
 */
export const centerlineLengthPx = (points) => {
  if (!points || points.length < 2) {
    return 0;
  }
  let length = 0;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    length += Math.sqrt(dx * dx + dy * dy);
  }
  return length;
};

/**
 * Maße des Weges aus Mittellinie und Breite.
 *
 * Fläche = Länge × Breite (Band konstanter Breite). Umfang = 2·Länge + 2·Breite
 * (Rechteck-Näherung des aufgerollten Bandes) — Kappen/Ecken bleiben unberücksichtigt,
 * was für die Anzeige unerheblich ist. Alles in Pixeln; die Umrechnung nach m/m²
 * übernimmt der Aufrufer (Editor: 1 px = 1 cm).
 *
 * @param {Array<{x: number, y: number}>} points - Mittellinien-Punkte
 * @param {number} widthPx - Wegbreite in Pixeln
 * @returns {{ lengthPx: number, widthPx: number, areaPx: number, perimeterPx: number }}
 */
export const wegMetricsPx = (points, widthPx) => {
  const lengthPx = centerlineLengthPx(points);
  const width = widthPx > 0 ? widthPx : 0;
  return {
    lengthPx,
    widthPx: width,
    areaPx: lengthPx * width,
    perimeterPx: lengthPx > 0 ? 2 * lengthPx + 2 * width : 0,
  };
};
